import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { EquityCurve } from '../components/charts/EquityCurve';
import { Container } from '../components/primitives/Container';
import { SectionHead } from '../components/primitives/SectionHead';
import { useContent } from '../content/load';
import { equityCurve } from '../lib/equity-curve';
import { useDocumentTitle } from '../lib/seo';
import type { Strategy } from '../content/schemas';

type SortKey = 'name' | 'sharpe' | 'cagr' | 'maxDD' | 'winRate';

const Wrap = styled.div`
  padding-bottom: ${(p) => p.theme.step[8]}px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${(p) => p.theme.step[3]}px;

  th, td {
    padding: 10px 12px;
    border-bottom: 1px solid ${(p) => p.theme.color.border.faint};
  }

  th {
    text-align: left;
    font-size: ${(p) => p.theme.step[0]}px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${(p) => p.theme.color.text[3]};
    cursor: pointer;
  }

  td.num {
    text-align: right;
    font-family: ${(p) => p.theme.font.mono};
  }

  th.num {
    text-align: right;
  }
`;

function sortBy(arr: Strategy[], key: SortKey, desc: boolean): Strategy[] {
  const sorted = [...arr].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (typeof av === 'number' && typeof bv === 'number') return av - bv;
    return String(av).localeCompare(String(bv));
  });
  return desc ? sorted.reverse() : sorted;
}

export default function Performance() {
  const { strategies, pages } = useContent();
  useDocumentTitle(pages.performance?.seo.title, pages.performance?.seo.description);
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({
    key: 'sharpe',
    desc: true,
  });

  const composite = useMemo(() => {
    if (strategies.length === 0) return equityCurve({ seed: 7 });
    const totalCap = strategies.reduce((s, x) => s + x.minCapital, 0);
    const curves = strategies.map((s) =>
      equityCurve({ seed: s.id.length * 17 + s.trades, steps: 240 }),
    );
    const len = curves[0]?.length ?? 0;
    const out: number[] = [];
    for (let i = 0; i < len; i++) {
      let sum = 0;
      strategies.forEach((s, k) => {
        const v = curves[k]?.[i] ?? 0;
        sum += v * (s.minCapital / totalCap);
      });
      out.push(sum);
    }
    return out;
  }, [strategies]);

  const sorted = sortBy(strategies, sort.key, sort.desc);
  const flip = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, desc: !s.desc } : { key, desc: true }));

  return (
    <Container>
      <Wrap>
        <SectionHead
          eyebrow="PERFORMANCE"
          heading="Composite equity curve."
          body="Capital-weighted across the full book."
        />
        <EquityCurve data={composite} height={300} />

        <Table>
          <thead>
            <tr>
              <th onClick={() => flip('name')}>Strategy</th>
              <th className="num" onClick={() => flip('sharpe')}>Sharpe</th>
              <th className="num" onClick={() => flip('cagr')}>CAGR</th>
              <th className="num" onClick={() => flip('maxDD')}>Max DD</th>
              <th className="num" onClick={() => flip('winRate')}>Win</th>
              <th className="num">Lifetime</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((s) => (
              <tr key={s.id}>
                <td>{s.name}</td>
                <td className="num">{s.sharpe.toFixed(2)}</td>
                <td className="num">{s.cagr}%</td>
                <td className="num">{s.maxDD}%</td>
                <td className="num">{s.winRate}%</td>
                <td className="num">{s.lifetime}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Wrap>
    </Container>
  );
}
